import { CHAIR_TYPE_LIST_CODE, type AppSettingList, type SettingListOption } from '@peles/shared';
import type { CanvasItem } from './types';
import { readSettingList } from './settingLists';
import { CHAIR_PARENT_FIELD } from './helpers';
import { isTableItem } from './types';

export { CHAIR_TYPE_LIST_CODE };

export type ChairTypeOption = SettingListOption;

/** The office's chair types, from the settings lists; none if it is missing or malformed. */
export function readChairTypes(lists: readonly AppSettingList[]): ChairTypeOption[] {
  return readSettingList(lists, CHAIR_TYPE_LIST_CODE);
}

/** Whether an item's chairs are counted and may be given a type. */
export function takesChairType(item: CanvasItem): boolean {
  if (item.objectType === 'CHAIR') return false;
  return (
    isTableItem(item) ||
    item.objectType === 'CHAIR_ROW' ||
    item.objectType === 'CEREMONY_CHAIRS' ||
    !!item.config?.canHaveChairs
  );
}

/** The chair type an item was set to, or null. */
export function chairTypeOf(item: CanvasItem): string | null {
  const value = item.config?.chairType;
  return typeof value === 'string' && value ? value : null;
}

/**
 * How many chairs an item brings: the chairs placed round it when there are
 * any (a seat removed by hand is a chair not ordered), else what it seats —
 * a table still waiting for its chairs needs them all the same, unless its
 * last chair was taken away by hand. A ceremony block counts its positions.
 */
export function chairCountOf(
  items: readonly CanvasItem[],
  item: CanvasItem,
  placedByParent: ReadonlyMap<string, number> = placedChairCounts(items),
): number {
  if (item.objectType === 'CEREMONY_CHAIRS' && Array.isArray(item.config?.positions)) {
    return item.config.positions.length;
  }
  const placed = item.uid ? (placedByParent.get(item.uid) ?? 0) : 0;
  if (placed > 0) return placed;
  if (item.config?.chairsRemovedByHand) return 0;
  return Math.max(0, item.seats || 0);
}

/**
 * How many chairs are placed round each parent, keyed by its uid — one pass
 * over the plan, so a tally of a large plan does not scan it once per table.
 * The same link `chairsOf` follows (`config.parentUid`).
 */
export function placedChairCounts(items: readonly CanvasItem[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const it of items) {
    if (it.objectType !== 'CHAIR') continue;
    const parent = it.config?.[CHAIR_PARENT_FIELD];
    if (typeof parent === 'string' && parent) counts.set(parent, (counts.get(parent) ?? 0) + 1);
  }
  return counts;
}

export interface ChairTypeTally {
  /** The stored value; null for items whose chair type is not chosen. */
  value: string | null;
  label: string;
  /** Tables, rows and blocks of this type. */
  items: number;
  chairs: number;
  /** A value no longer in the Settings list. */
  unknown: boolean;
}

/**
 * The plan's chairs by type, for the rental order: every type in the Settings
 * list in its order (even at nought, so the list reads the same plan to plan),
 * then any stored type the list no longer has, then those not chosen.
 */
export function tallyChairTypes(
  items: readonly CanvasItem[],
  options: readonly ChairTypeOption[],
): { rows: ChairTypeTally[]; totalChairs: number } {
  const byValue = new Map<string | null, ChairTypeTally>();
  for (const o of options) {
    byValue.set(o.value, { value: o.value, label: o.label, items: 0, chairs: 0, unknown: false });
  }
  let totalChairs = 0;
  const placed = placedChairCounts(items);
  for (const item of items) {
    if (!takesChairType(item)) continue;
    const chairs = chairCountOf(items, item, placed);
    const value = chairTypeOf(item);
    let row = byValue.get(value);
    if (!row) {
      row =
        value === null
          ? { value: null, label: 'Not chosen', items: 0, chairs: 0, unknown: false }
          : { value, label: value, items: 0, chairs: 0, unknown: true };
      byValue.set(value, row);
    }
    row.items += 1;
    row.chairs += chairs;
    totalChairs += chairs;
  }
  const rows = [...byValue.values()];
  const order = (r: ChairTypeTally) => (r.value === null ? 2 : r.unknown ? 1 : 0);
  // Settings order first (Map keeps insertion order), then unknown, then not chosen.
  return {
    rows: rows
      .map((r, i) => ({ r, i }))
      .sort((a, b) => order(a.r) - order(b.r) || a.i - b.i)
      .map(({ r }) => r),
    totalChairs,
  };
}
